<template>
  <div
    class="ws-assistant-message"
    :class="message.role === 'user' ? 'ws-assistant-message--user' : 'ws-assistant-message--assistant'"
  >
    <div
      v-if="message.role === 'user'"
      class="ws-assistant-bubble"
    >{{ message.text }}</div>

    <div
      v-else
      class="ws-assistant-bubble"
    >
      <!-- eslint-disable-next-line vue/no-v-html -- rendered by renderAssistantMarkdown (no raw HTML, WireSock links only, DOMPurify) -->
      <div
        v-if="message.text"
        class="ws-assistant-text"
        v-html="html"
      />
      <div
        v-else-if="message.streaming"
        class="text-grey"
      >
        <v-progress-circular
          indeterminate
          size="16"
          width="2"
          class="mr-2"
        />Thinking…
      </div>

      <v-alert
        v-if="message.error"
        type="warning"
        variant="tonal"
        density="compact"
        class="mt-2"
      >
        {{ message.error.message }}
        <template v-if="message.error.retryAfterSec">
          Try again in {{ formatWait(message.error.retryAfterSec) }}.
        </template>
        <div class="mt-1">
          <a
            href="/documentation/"
            target="_blank"
            rel="noopener"
          >Documentation</a>
          ·
          <a
            href="#"
            @click.prevent="$emit('human')"
          >Talk to a human</a>
        </div>
      </v-alert>

      <div
        v-if="message.sources.length > 0"
        class="ws-assistant-sources mt-2"
      >
        <div class="text-caption text-grey">Sources</div>
        <ul>
          <li
            v-for="source in message.sources"
            :key="source.id"
          >
            <a
              v-if="source.url && isWireSockUrl(source.url)"
              :href="source.url"
              target="_blank"
              rel="noopener"
            >{{ source.title }}</a>
            <span v-else>{{ source.title }}</span>
          </li>
        </ul>
      </div>

      <div
        v-if="!message.streaming && message.messageId && !message.error"
        class="d-flex align-center ga-1 mt-1"
      >
        <v-btn
          size="x-small"
          variant="text"
          icon="mdi-thumb-up-outline"
          :color="rating === 'up' ? 'primary' : undefined"
          aria-label="Helpful"
          @click="$emit('rate', 'up')"
        />
        <v-btn
          size="x-small"
          variant="text"
          icon="mdi-thumb-down-outline"
          :color="rating === 'down' ? 'error' : undefined"
          aria-label="Not helpful"
          @click="$emit('rate', 'down')"
        />
        <v-btn
          size="x-small"
          variant="text"
          class="text-none"
          @click="$emit('report')"
        >Report wrong answer</v-btn>
      </div>

      <v-btn
        v-if="!message.streaming && (message.offerHuman || rating === 'down')"
        size="small"
        variant="tonal"
        color="primary"
        class="mt-2 text-none"
        prepend-icon="mdi-account-voice"
        @click="$emit('human')"
      >Talk to a human</v-btn>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import type { AssistantChatMessage } from '~/composables/useAssistantChat';
import { renderAssistantMarkdown } from '~~/shared/lib/assistant-markdown';
import { isWireSockUrl } from '~~/shared/lib/assistant-links';

const props = defineProps<{
  message: AssistantChatMessage;
  rating?: 'up' | 'down' | null;
}>();

defineEmits<{
  human: [];
  rate: [rating: 'up' | 'down'];
  report: [];
}>();

const html = computed(() => renderAssistantMarkdown(props.message.text));

function formatWait(seconds: number): string {
  return seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds} s`;
}
</script>

<style scoped>
.ws-assistant-message {
  display: flex;
  margin-bottom: 12px;
}

.ws-assistant-message--user {
  justify-content: flex-end;
}

.ws-assistant-bubble {
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.ws-assistant-message--user .ws-assistant-bubble {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  white-space: pre-wrap;
}

.ws-assistant-message--assistant .ws-assistant-bubble {
  background: #f3f4f6;
}

.ws-assistant-text :deep(p) {
  margin-bottom: 8px;
}

.ws-assistant-text :deep(ol),
.ws-assistant-text :deep(ul),
.ws-assistant-sources ul {
  padding-left: 20px;
  margin-bottom: 8px;
}

.ws-assistant-text :deep(pre) {
  background: #e5e7eb;
  padding: 8px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 12px;
}

.ws-assistant-text :deep(code) {
  font-size: 12px;
}
</style>
